// Right-hand analytics panel — the Carto-style "insights" surface. An icon-tab
// nav switches between six boards; each leads with one big number (Neurath: one
// number per screen) and a few mini charts, and recomputes live from the
// currently *filtered* farm set so toggling a layer/filter updates the numbers.
//
// Boards: Biomasse & verdi · Slaktekalender · Støy · Operasjoner nå · Miljø &
// helse · Compliance. The slaughter-forecast chart (with its kt/MNOK toggle and
// FX scenario slider) is moved here from its old floating panel.

import {
  mountHarvestChart,
  renderHarvestChart,
  type HarvestItem,
} from "./harvestChart";
import { insightCompanyUrl } from "./insight";

// Structural subset of EnrichedProperties (main.ts) — passed in by main.
export type AnalyticsPoint = {
  name: string;
  municipality: string | null;
  est_biomass_t: number | null;
  harvest_year: number | null;
  harvest_month: number | null;
  harvest_t: number | null;
  db: number | null;
  tier: string;
  is_fallow: boolean;
  has_lice: boolean;
  has_treat: boolean;
  has_pd: boolean;
  has_ila: boolean;
  avg_lice: number | null;
  farm_op: "delousing" | "delousing_due" | "harvest" | "smolt" | "service" | null;
  operator_names: string[];
  operator_slugs: string[];
  species_groups: string[];
};

type AuditItem = {
  company_name: string;
  audit_year: number;
  n_avvik: number;
  n_forbedringspunkt: number;
  report_url: string | null;
  operator_slug: string | null;
  operator_name: string | null;
};
type AuditsResponse = { count: number; items: AuditItem[] };

export type VesselSummaryLite = {
  total: number;
  laden: number;
  byRole: Record<string, number>;
  byState: Record<string, number>;
  byCurrentOp: Record<string, number>;
};

export type AnalyticsDeps = {
  fetchJSON: <T>(path: string, init?: RequestInit) => Promise<T>;
  readCache: <T>(key: string) => T | undefined;
  writeCache: <T>(key: string, value: T) => void;
  titleCaseOperator: (name: string) => string;
  farmOpMeta: Record<string, { label: string; color: string }>;
  monthsFull: string[];
  vesselSummary: () => VesselSummaryLite;
  // Drive the map's operation filter from the "Operasjoner nå" board: the given
  // ops are shown on the map (others ghosted); an empty array = show all.
  onOpFilter?: (ops: string[]) => void;
};

type Board = {
  id: string;
  label: string;
  icon: string;
  el: HTMLElement;
  update: (pts: AnalyticsPoint[]) => void;
  csv: () => { name: string; text: string };
};

// Tier order + colour (mirrors the dB ramp in main.ts / style.css).
const TIER_ORDER = ["Stille", "Moderat", "Forhøyet", "Høy", "Kritisk", "Ingen data"];
const TIER_COLOR: Record<string, string> = {
  Stille: "#2c7bb6",
  Moderat: "#abd9e9",
  Forhøyet: "#ffffbf",
  Høy: "#fdae61",
  Kritisk: "#d7191c",
  "Ingen data": "#5a6570",
};

const LICE_THRESHOLD = 0.5; // adult female lice · regulatory action limit

const ICONS = {
  verdi: '<path d="M3 3v18h18"/><rect x="7" y="11" width="3" height="6"/><rect x="12" y="7" width="3" height="10"/><rect x="17" y="13" width="3" height="4"/>',
  slakt: '<rect x="3" y="4" width="18" height="17" rx="2"/><path d="M3 9h18M8 2v4M16 2v4"/>',
  støy: '<path d="M2 12h3l3-8 4 16 3-8h7"/>',
  ops: '<circle cx="12" cy="12" r="3"/><path d="M4.5 4.5a10 10 0 0 0 0 15M19.5 4.5a10 10 0 0 1 0 15M7.5 7.5a6 6 0 0 0 0 9M16.5 7.5a6 6 0 0 1 0 9"/>',
  vessels: '<path d="M3 14l1.8 5h14.4L21 14M5 14V8a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v6M12 3v4"/>',
  helse: '<path d="M3 12h4l2 6 4-14 2 8h6"/>',
  compliance: '<path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"/><path d="M9 12l2 2 4-4"/>',
};

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let deps: AnalyticsDeps;
let boards: Board[] = [];
let activeId = "ops"; // "Operasjoner nå" is the daily-return hook → default
let lastPoints: AnalyticsPoint[] = [];
let auditsCache: AuditItem[] | null = null;
let onBoardChange: ((id: string) => void) | undefined;
let scopeEl: HTMLElement | null = null; // "N anlegg i kartutsnittet"

// ---------------------------------------------------------------------------
// Small DOM + format helpers
// ---------------------------------------------------------------------------

function h(tag: string, className?: string, html?: string): HTMLElement {
  const e = document.createElement(tag);
  if (className) e.className = className;
  if (html != null) e.innerHTML = html;
  return e;
}

const nf = (n: number, max = 0): string =>
  n.toLocaleString("no", { maximumFractionDigits: max });

// The big "one number" is rendered from a raw numeric value + a format kind, so
// it can be animated (count-up) on change. `null` → an em-dash, not animated.
type StatKind = "int" | "kt" | "db" | "pct" | "mnd";
function formatStat(kind: StatKind, n: number): string {
  switch (kind) {
    case "int": return nf(n);
    case "kt": return `${nf(n, 0)} kt`;
    case "db": return `${n.toFixed(0)} dB`;
    case "pct": return `${Math.round(n)}%`;
    case "mnd": return `${Math.round(n)} mnd`;
  }
}

function statBlock(value: number | null, kind: StatKind, label: string, hint?: string): string {
  const numHtml =
    value == null
      ? `<div class="astat-num">—</div>`
      : `<div class="astat-num" data-val="${value}" data-kind="${kind}">${formatStat(kind, value)}</div>`;
  return (
    `<div class="astat">${numHtml}` +
    `<div class="astat-label">${label}</div>` +
    (hint ? `<div class="astat-hint">${hint}</div>` : "") +
    `</div>`
  );
}

// Count-up tween for a board's big number — animates from the previously shown
// value to the new one (so panning that doesn't change a number stays still).
const statPrev = new Map<string, number>();
const prefersReducedMotion = (): boolean =>
  typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

function animateStats(board: Board): void {
  const el = board.el.querySelector<HTMLElement>(".astat-num[data-val]");
  if (!el) return;
  const to = Number(el.dataset.val);
  const kind = (el.dataset.kind ?? "int") as StatKind;
  if (!Number.isFinite(to)) return;
  const prev = statPrev.get(board.id);
  statPrev.set(board.id, to);
  if (prev == null || Math.abs(prev - to) < 1e-9 || prefersReducedMotion()) {
    el.textContent = formatStat(kind, to);
    return;
  }
  const from = prev;
  const dur = 520;
  const t0 = performance.now();
  el.textContent = formatStat(kind, from);
  const tick = (now: number): void => {
    const t = Math.min(1, (now - t0) / dur);
    const e = 1 - Math.pow(1 - t, 3); // easeOutCubic
    el.textContent = formatStat(kind, from + (to - from) * e);
    if (t < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/** Horizontal label+value bars (Carto "Stores by location" style). */
function barRows(
  rows: { label: string; value: number; valueText?: string; color?: string }[],
): string {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return rows
    .map((r) => {
      const w = Math.round((r.value / max) * 100);
      const col = r.color ?? "#43c6a8";
      return (
        `<div class="abar">` +
        `<div class="abar-top"><span class="abar-label">${r.label}</span>` +
        `<span class="abar-val">${r.valueText ?? nf(r.value)}</span></div>` +
        `<div class="abar-track"><span class="abar-fill" style="width:${w}%;background:${col}"></span></div>` +
        `</div>`
      );
    })
    .join("");
}

function card(title: string, sub: string, bodyHtml: string): string {
  return (
    `<section class="acard"><div class="acard-head">${title}</div>` +
    (sub ? `<div class="acard-sub">${sub}</div>` : "") +
    `<div class="acard-body">${bodyHtml}</div></section>`
  );
}

function escHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] ?? c,
  );
}

/** Aggregate points by operator (slug-keyed, name fallback). */
function byOperator(pts: AnalyticsPoint[]): Map<string, { name: string; slug: string; n: number }> {
  const m = new Map<string, { name: string; slug: string; n: number }>();
  for (const p of pts) {
    const names = p.operator_names ?? [];
    const slugs = p.operator_slugs ?? [];
    const n = Math.max(names.length, slugs.length, names.length ? 1 : 0);
    for (let i = 0; i < n; i++) {
      const slug = slugs[i] ?? "";
      const name = names[i] ?? slug;
      if (!name) continue;
      const key = slug || name;
      const cur = m.get(key) ?? { name: deps.titleCaseOperator(name), slug, n: 0 };
      cur.n += 1;
      m.set(key, cur);
    }
  }
  return m;
}

/** Operator row with an → Insight deep-link when we have a slug. */
function operatorRow(name: string, slug: string, valueText: string): string {
  const inner =
    `<span class="aop-name">${escHtml(name)}</span>` +
    `<span class="aop-val">${valueText}</span>`;
  return slug
    ? `<a class="aop aop-link" href="${insightCompanyUrl(slug)}" target="_blank" rel="noopener">${inner}<span class="aop-go">→</span></a>`
    : `<div class="aop">${inner}</div>`;
}

function csvEscape(v: string | number): string {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function toCSV(headers: string[], rows: (string | number)[][]): string {
  return [headers, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
}

// ---------------------------------------------------------------------------
// Boards
// ---------------------------------------------------------------------------

function buildBiomasseBoard(): Board {
  const el = h("div", "aboard");
  const top = h("div");
  const chartCard = h("section", "acard");
  chartCard.appendChild(h("div", "acard-head", "Slaktevolum &amp; verdi · neste 18 mnd"));
  const chartBody = h("div", "acard-body");
  chartCard.appendChild(chartBody);
  el.appendChild(top);
  el.appendChild(chartCard);
  mountHarvestChart(chartBody);

  let lastTotal = 0;
  let lastEst = 0;
  const update = (pts: AnalyticsPoint[]): void => {
    const withEst = pts.filter((p) => (p.est_biomass_t ?? 0) > 0);
    const total = withEst.reduce((a, p) => a + (p.est_biomass_t ?? 0), 0);
    lastTotal = total;
    lastEst = withEst.length;
    top.innerHTML = statBlock(
      total / 1000,
      "kt",
      "stående biomasse (anslag)",
      `${nf(withEst.length)} av ${nf(pts.length)} anlegg med estimat`,
    );
    const items: HarvestItem[] = pts.map((p) => ({
      harvest_year: p.harvest_year,
      harvest_month: p.harvest_month,
      harvest_t: p.harvest_t,
    }));
    renderHarvestChart(items, new Date());
  };

  return {
    id: "verdi",
    label: "Biomasse & verdi",
    icon: ICONS.verdi,
    el,
    update,
    csv: () => ({
      name: "biomasse",
      text: toCSV(
        ["metrikk", "verdi"],
        [
          ["stående_biomasse_t", Math.round(lastTotal)],
          ["anlegg_med_estimat", lastEst],
        ],
      ),
    }),
  };
}

function buildSlaktBoard(): Board {
  const el = h("div", "aboard");
  let csvRows: (string | number)[][] = [];
  const update = (pts: AnalyticsPoint[]): void => {
    const now = new Date();
    const anchor = now.getFullYear() * 12 + now.getMonth();
    const HORIZON = 18;
    const counts = new Array<number>(HORIZON).fill(0);
    const months: number[] = []; // months-to-harvest per site (for median)
    for (const p of pts) {
      if (p.harvest_year == null || p.harvest_month == null || !p.harvest_t) continue;
      let idx = p.harvest_year * 12 + (p.harvest_month - 1) - anchor;
      if (idx < 0) idx = 0;
      months.push(idx);
      if (idx < HORIZON) counts[idx] = (counts[idx] ?? 0) + 1;
    }
    months.sort((a, b) => a - b);
    const median = months.length ? months[Math.floor(months.length / 2)] : null;
    const peak = counts.indexOf(Math.max(0, ...counts));
    const moAt = (i: number): string => deps.monthsFull[(anchor + i) % 12] ?? "";
    const peakLbl =
      counts.some((c) => c > 0)
        ? `${moAt(peak)} ${Math.floor((anchor + peak) / 12)}`
        : "—";

    const rows = counts.map((c, i) => ({
      label: `${moAt(i).slice(0, 3)} '${String(Math.floor((anchor + i) / 12)).slice(2)}`,
      value: c,
      valueText: `${c}`,
      color: i === peak && c > 0 ? "#e76f51" : "#43c6a8",
    }));
    csvRows = rows.map((r) => [r.label, r.value]);

    el.innerHTML =
      statBlock(
        median == null ? null : median,
        "mnd",
        "median tid til slakt",
        `${nf(months.length)} anlegg med slaktevindu`,
      ) +
      card(
        "Slaktekalender",
        `Topp: ${peakLbl}`,
        barRows(rows.filter((r) => r.value > 0)) ||
          `<div class="aempty">Ingen slaktevindu i utvalget.</div>`,
      );
  };
  return {
    id: "slakt",
    label: "Slaktekalender",
    icon: ICONS.slakt,
    el,
    update,
    csv: () => ({ name: "slaktekalender", text: toCSV(["måned", "antall_anlegg"], csvRows) }),
  };
}

function buildStøyBoard(): Board {
  const el = h("div", "aboard");
  let csvRows: (string | number)[][] = [];
  const update = (pts: AnalyticsPoint[]): void => {
    const dbs = pts.map((p) => p.db).filter((d): d is number => d != null).sort((a, b) => a - b);
    const median = dbs.length ? dbs[Math.floor(dbs.length / 2)] : null;
    const tierCount = new Map<string, number>();
    for (const p of pts) tierCount.set(p.tier, (tierCount.get(p.tier) ?? 0) + 1);
    const measured = pts.filter((p) => p.db != null).length;
    const loud = (tierCount.get("Høy") ?? 0) + (tierCount.get("Kritisk") ?? 0);
    const loudPct = measured ? Math.round((100 * loud) / measured) : 0;

    const rows = TIER_ORDER.filter((t) => (tierCount.get(t) ?? 0) > 0).map((t) => ({
      label: t,
      value: tierCount.get(t) ?? 0,
      color: TIER_COLOR[t] ?? "#5a6570",
    }));
    csvRows = rows.map((r) => [r.label, r.value]);

    el.innerHTML =
      statBlock(
        median == null ? null : median,
        "db",
        "median 7-dagers støynivå (Leq)",
        `${nf(measured)} av ${nf(pts.length)} anlegg med måling`,
      ) +
      card(
        "Fordeling på uro-tier",
        `${loudPct}% i Høy/Kritisk`,
        barRows(rows) || `<div class="aempty">Ingen målinger i utvalget.</div>`,
      );
  };
  return {
    id: "støy",
    label: "Støy",
    icon: ICONS.støy,
    el,
    update,
    csv: () => ({ name: "stoy", text: toCSV(["tier", "antall_anlegg"], csvRows) }),
  };
}

function buildOpsBoard(): Board {
  const el = h("div", "aboard");
  let csvRows: (string | number)[][] = [];
  // Which operations are isolated on the map (multi-select; click cards to
  // toggle). Lives in the board so it survives the per-viewport re-render.
  const selectedOps = new Set<string>();
  let curPts: AnalyticsPoint[] = [];
  // Ops in progress, then "delousing_due" (a planning/warning class) last.
  const ops = ["delousing", "harvest", "smolt", "service", "delousing_due"] as const;
  const ACTIVE_OPS = ["delousing", "harvest", "smolt", "service"]; // count for the headline
  const opMeta = (op: string): { label: string; color: string } =>
    deps.farmOpMeta[op] ?? { label: op, color: "#5a6570" };

  const render = (): void => {
    const byOp = new Map<string, AnalyticsPoint[]>();
    for (const p of curPts) {
      if (!p.farm_op) continue;
      const arr = byOp.get(p.farm_op) ?? [];
      arr.push(p);
      byOp.set(p.farm_op, arr);
    }
    const liveTotal = ACTIVE_OPS.reduce((a, o) => a + (byOp.get(o)?.length ?? 0), 0);
    const hasSel = selectedOps.size > 0;
    const selList = ops.filter((o) => selectedOps.has(o));

    csvRows = [];
    // The big number tracks the selected ops when any are isolated.
    const selCount = hasSel
      ? selList.reduce((a, o) => a + (byOp.get(o)?.length ?? 0), 0)
      : liveTotal;
    const selLabel =
      selList.length === 1
        ? `anlegg med ${opMeta(selList[0]!).label.toLowerCase()} nå`
        : "anlegg med valgte operasjoner nå";
    let html = statBlock(
      selCount,
      "int",
      hasSel ? selLabel : "anlegg med pågående operasjon nå",
      "fra AIS-bevegelser · oppdateres løpende",
    );

    // Filter affordance: a hint when nothing is picked, or the active selection
    // (coloured chips) + a "show all" pill.
    html += hasSel
      ? `<div class="aopfilter-bar is-on">` +
          `<span class="aopfilter-tag">Viser ` +
          selList
            .map(
              (o) =>
                `<b class="aopfilter-chip" style="--opc:${opMeta(o).color}">${escHtml(opMeta(o).label)}</b>`,
            )
            .join("") +
          ` på kartet</span>` +
          `<button type="button" class="aop-clear" data-opfilter="__all__">✕ Vis alle</button>` +
        `</div>`
      : `<div class="aopfilter-bar"><span class="aopfilter-hint">` +
          `Trykk operasjoner for å vise bare dem på kartet</span></div>`;

    for (const op of ops) {
      const meta = opMeta(op);
      const arr = byOp.get(op) ?? [];
      const opsAgg = [...byOperator(arr).values()].sort((a, b) => b.n - a.n).slice(0, 6);
      for (const o of opsAgg) csvRows.push([meta.label, o.name, o.n]);
      const isSel = selectedOps.has(op);
      const dimmed = hasSel && !isSel;
      const rowsHtml =
        opsAgg.length > 0
          ? opsAgg.map((o) => operatorRow(o.name, o.slug, `${o.n}`)).join("")
          : `<div class="aempty">Ingen akkurat nå.</div>`;
      // Collapse the breakdown of the non-selected ops so the panel focuses on
      // the chosen ones; everything expands again when the filter is cleared.
      const bodyHtml = dimmed ? "" : `<div class="acard-body"><div class="aops">${rowsHtml}</div></div>`;
      html +=
        `<section class="acard aopcard${isSel ? " is-selected" : ""}${dimmed ? " is-dimmed" : ""}" style="--opc:${meta.color}">` +
        `<button type="button" class="aopcard-head" data-opfilter="${op}" aria-pressed="${isSel}">` +
          `<span class="aopcard-box">${isSel ? "✓" : ""}</span>` +
          `<span class="adot" style="background:${meta.color}"></span>` +
          `<span class="aopcard-label">${escHtml(meta.label)}</span>` +
          `<span class="aopcard-count">${arr.length}</span>` +
        `</button>` +
        bodyHtml +
        `</section>`;
    }
    el.innerHTML = html;
  };

  const update = (pts: AnalyticsPoint[]): void => {
    curPts = pts;
    render();
    // Keep the map in sync whenever this board (re)renders while active.
    deps.onOpFilter?.([...selectedOps]);
  };

  const board: Board = {
    id: "ops",
    label: "Operasjoner nå",
    icon: ICONS.ops,
    el,
    update,
    csv: () => ({ name: "operasjoner", text: toCSV(["operasjon", "eier", "antall_anlegg"], csvRows) }),
  };

  // One delegated handler: a click on an op card (or the "show all" pill) toggles
  // the map filter. Operator rows are <a> links outside the header, so they're
  // unaffected.
  el.addEventListener("click", (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>("[data-opfilter]");
    if (!target) return;
    const op = target.dataset.opfilter ?? "";
    if (op === "__all__") selectedOps.clear();
    else if (selectedOps.has(op)) selectedOps.delete(op);
    else selectedOps.add(op);
    deps.onOpFilter?.([...selectedOps]);
    render();
    animateStats(board);
  });

  return board;
}

function buildVesselsBoard(): Board {
  const el = h("div", "aboard");
  const ROLE_LBL: Record<string, string> = {
    wellboat: "Brønnbåt / storfartøy",
    carrier: "Mellomfartøy",
    service: "Servicebåt",
  };
  const STATE_LBL: Record<string, [string, string]> = {
    transit: ["Underveis", "#43c6a8"],
    farm: ["Ved anlegg", "#5bb98a"],
    reception: ["Ved slakteri / mottak", "#E63926"],
    idle: ["I ro / havn", "#8b95a1"],
  };
  const OP_LBL: Record<string, [string, string]> = {
    slaughter: ["Til slakt", "#E63926"],
    delousing: ["Avlusing", "#F4A261"],
    harvest: ["Høsting", "#C77DFF"],
    smolt: ["Smolt", "#7BE3C6"],
  };
  let csvRows: (string | number)[][] = [];
  const update = (): void => {
    const s = deps.vesselSummary();
    if (scopeEl) scopeEl.innerHTML = scopeRow(`${nf(s.total)} fartøy i kartutsnittet`);
    const roleRows = Object.keys(ROLE_LBL).map((k) => ({
      label: ROLE_LBL[k] ?? k,
      value: s.byRole[k] ?? 0,
    }));
    const stateRows = Object.keys(STATE_LBL).map((k) => ({
      label: STATE_LBL[k]?.[0] ?? k,
      value: s.byState[k] ?? 0,
      color: STATE_LBL[k]?.[1] ?? "#43c6a8",
    }));
    const opRows = Object.keys(OP_LBL)
      .map((k) => ({ label: OP_LBL[k]?.[0] ?? k, value: s.byCurrentOp[k] ?? 0, color: OP_LBL[k]?.[1] ?? "#43c6a8" }))
      .filter((r) => r.value > 0);
    csvRows = [
      ...roleRows.map((r) => ["type", r.label, r.value] as (string | number)[]),
      ...stateRows.map((r) => ["status", r.label, r.value] as (string | number)[]),
      ...opRows.map((r) => ["operasjon", r.label, r.value] as (string | number)[]),
    ];

    el.innerHTML =
      statBlock(
        s.total,
        "int",
        "fartøy i flåten nå",
        `${nf(s.laden)} antatt med fisk om bord`,
      ) +
      card("Etter type", "", barRows(roleRows)) +
      card("Status nå", "", barRows(stateRows)) +
      (opRows.length
        ? card("Pågående operasjon", "", barRows(opRows))
        : "");
  };
  return {
    id: "vessels",
    label: "Skipstrafikk",
    icon: ICONS.vessels,
    el,
    update,
    csv: () => ({ name: "skipstrafikk", text: toCSV(["kategori", "verdi", "antall"], csvRows) }),
  };
}

function buildHelseBoard(): Board {
  const el = h("div", "aboard");
  let csvRows: (string | number)[][] = [];
  const update = (pts: AnalyticsPoint[]): void => {
    const salmonid = pts.filter((p) => p.species_groups?.includes("laksefisk"));
    const active = salmonid.filter((p) => !p.is_fallow);
    const withLice = active.filter((p) => p.has_lice).length;
    const licePct = active.length ? Math.round((100 * withLice) / active.length) : 0;
    const liceVals = active.map((p) => p.avg_lice).filter((v): v is number => v != null);
    const avgLice = liceVals.length ? liceVals.reduce((a, b) => a + b, 0) / liceVals.length : null;
    const overThresh = liceVals.filter((v) => v > LICE_THRESHOLD).length;
    const pd = pts.filter((p) => p.has_pd).length;
    const ila = pts.filter((p) => p.has_ila).length;
    const fallow = salmonid.filter((p) => p.is_fallow).length;

    const rows = [
      { label: `Lus over grense (>${LICE_THRESHOLD})`, value: overThresh, color: "#e76f51" },
      { label: "PD påvist", value: pd, color: "#f4a261" },
      { label: "ILA påvist", value: ila, color: "#c77dff" },
      { label: "Brakklagt", value: fallow, color: "#5a6570" },
    ];
    csvRows = rows.map((r) => [r.label, r.value]);

    el.innerHTML =
      statBlock(
        licePct,
        "pct",
        "aktive laks-anlegg med lus rapportert",
        avgLice == null
          ? `${nf(active.length)} aktive anlegg`
          : `snitt ${avgLice.toFixed(2)} voksne hunnlus · grense ${LICE_THRESHOLD}`,
      ) +
      card("Helse & miljø", "På tvers av utvalget", barRows(rows));
  };
  return {
    id: "helse",
    label: "Miljø & helse",
    icon: ICONS.helse,
    el,
    update,
    csv: () => ({ name: "miljo-helse", text: toCSV(["indikator", "antall"], csvRows) }),
  };
}

function buildComplianceBoard(): Board {
  const el = h("div", "aboard");
  let loaded = false;
  let csvRows: (string | number)[][] = [];

  const render = (): void => {
    if (!auditsCache) {
      el.innerHTML = `<div class="aempty">Laster tilsynsdata …</div>`;
      return;
    }
    const agg = new Map<string, { name: string; slug: string; avvik: number; fbp: number; url: string | null }>();
    for (const a of auditsCache) {
      const key = a.operator_slug ?? a.company_name;
      const cur =
        agg.get(key) ?? {
          name: deps.titleCaseOperator(a.operator_name ?? a.company_name),
          slug: a.operator_slug ?? "",
          avvik: 0,
          fbp: 0,
          url: a.report_url,
        };
      cur.avvik += a.n_avvik ?? 0;
      cur.fbp += a.n_forbedringspunkt ?? 0;
      if (!cur.url) cur.url = a.report_url;
      agg.set(key, cur);
    }
    const ranked = [...agg.values()].sort((a, b) => b.avvik - a.avvik);
    const totalAvvik = ranked.reduce((a, r) => a + r.avvik, 0);
    csvRows = ranked.map((r) => [r.name, r.avvik, r.fbp]);

    const rows = ranked
      .filter((r) => r.avvik > 0)
      .slice(0, 12)
      .map((r) => {
        const val = `<span class="ac-avvik">${r.avvik}</span><span class="ac-fbp">${r.fbp}</span>`;
        return operatorRow(r.name, r.slug, val);
      })
      .join("");

    el.innerHTML =
      statBlock(
        totalAvvik,
        "int",
        "avvik i Mattilsynets systemrevisjoner",
        `${nf(ranked.length)} selskaper revidert`,
      ) +
      card(
        "Mest avvik",
        '<span class="ac-key"><span class="ac-avvik">avvik</span> · <span class="ac-fbp">forbedringspunkt</span></span>',
        `<div class="aops">${rows || `<div class="aempty">Ingen avvik registrert.</div>`}</div>`,
      );
  };

  const ensureLoaded = (): void => {
    if (loaded) return;
    loaded = true;
    auditsCache = deps.readCache<AuditsResponse>("audits")?.items ?? auditsCache;
    if (auditsCache) render();
    deps
      .fetchJSON<AuditsResponse>("/v1/compliance/audits?limit=1000")
      .then((r) => {
        deps.writeCache("audits", r);
        auditsCache = r.items;
        render();
      })
      .catch((err) => {
        console.error("audits", err);
        if (!auditsCache) el.innerHTML = `<div class="aempty">Kunne ikke laste tilsynsdata.</div>`;
      });
  };

  return {
    id: "compliance",
    label: "Compliance",
    icon: ICONS.compliance,
    el,
    update: () => {
      ensureLoaded();
      if (auditsCache) render();
    },
    csv: () => ({
      name: "compliance",
      text: toCSV(["selskap", "avvik", "forbedringspunkt"], csvRows),
    }),
  };
}

// ---------------------------------------------------------------------------
// Panel assembly
// ---------------------------------------------------------------------------

export function mountAnalytics(
  container: HTMLElement,
  d: AnalyticsDeps,
  opts?: { initial?: string; onChange?: (id: string) => void },
): void {
  deps = d;
  onBoardChange = opts?.onChange;
  if (opts?.initial && opts.initial) activeId = opts.initial;

  boards = [
    buildOpsBoard(),
    buildVesselsBoard(),
    buildBiomasseBoard(),
    buildSlaktBoard(),
    buildStøyBoard(),
    buildHelseBoard(),
    buildComplianceBoard(),
  ];
  if (!boards.some((b) => b.id === activeId)) activeId = boards[0]?.id ?? activeId;

  const nav = h("div", "an-nav");
  for (const b of boards) {
    const btn = h("button", "an-tab") as HTMLButtonElement;
    btn.type = "button";
    btn.dataset.board = b.id;
    btn.title = b.label;
    btn.setAttribute("aria-label", b.label);
    btn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${b.icon}</svg>`;
    btn.addEventListener("click", () => setActiveBoard(b.id));
    nav.appendChild(btn);
  }

  const title = h("div", "an-title");
  scopeEl = h("div", "an-scope");
  const body = h("div", "an-body");
  for (const b of boards) body.appendChild(b.el);

  const foot = h("div", "an-foot");
  const dl = h("button", "an-download") as HTMLButtonElement;
  dl.type = "button";
  dl.innerHTML = "↓ Last ned (CSV)";
  dl.addEventListener("click", downloadActive);
  foot.appendChild(dl);

  container.classList.add("analytics");
  container.append(nav, title, scopeEl, body, foot);

  applyActive();
}

function applyActive(): void {
  const container = document.getElementById("analytics");
  if (!container) return;
  const active = boards.find((b) => b.id === activeId) ?? boards[0];
  if (!active) return;
  container.querySelectorAll<HTMLElement>(".an-tab").forEach((t) =>
    t.classList.toggle("is-active", t.dataset.board === active.id),
  );
  for (const b of boards) b.el.classList.toggle("is-active", b.id === active.id);
  const title = container.querySelector(".an-title");
  if (title) title.textContent = active.label;
  updateScope();
  active.update(lastPoints);
  animateStats(active);
}

export function setActiveBoard(id: string): void {
  if (!boards.some((b) => b.id === id)) return;
  activeId = id;
  applyActive();
  onBoardChange?.(id);
}

export function getActiveBoard(): string {
  return activeId;
}

/**
 * Recompute the active board from the farm set currently in the map viewport.
 * `points` is the filtered farm set restricted to the visible map bounds, so the
 * numbers always describe the area you're looking at.
 */
export function refreshAnalytics(points: AnalyticsPoint[]): void {
  lastPoints = points;
  updateScope();
  const active = boards.find((b) => b.id === activeId);
  if (active) {
    active.update(points);
    animateStats(active);
  }
}

// The "in view" scope line. Farm boards count anlegg; the vessels board sets its
// own (fartøy) count from its update; compliance is national, so no scope.
function setScope(html: string): void {
  if (scopeEl) scopeEl.innerHTML = html;
}
function scopeRow(text: string): string {
  return `<span class="an-scope-dot"></span>${text}`;
}
function updateScope(): void {
  if (!scopeEl) return;
  if (activeId === "vessels") return; // the vessels board owns its scope line
  if (activeId === "compliance") {
    scopeEl.innerHTML = "";
    return;
  }
  setScope(scopeRow(`${nf(lastPoints.length)} anlegg i kartutsnittet`));
}

/** Re-run the active board (e.g. when async vessel data lands). */
export function refreshActiveBoard(): void {
  const active = boards.find((b) => b.id === activeId);
  if (active) {
    active.update(lastPoints);
    animateStats(active);
  }
}

function downloadActive(): void {
  const active = boards.find((b) => b.id === activeId);
  if (!active) return;
  const { name, text } = active.csv();
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `phoniq-${name}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
